<template>
  <div class="exec-list-create-document tw-max-w-[640px] tw-relative tw-min-h-[120px]">
    <div
      class="exec-list-create-document__body tw-transition-[filter,opacity] tw-duration-200"
      :class="isLoadingUi ? 'tw-opacity-50 tw-blur-[3px] tw-pointer-events-none tw-select-none' : ''"
    >
      <CustomTabs
        v-if="createModeTabs.length > 1"
        :tab="createMode"
        :data="createModeTabs"
        alt
        paddingless
        @change_tab="onCreateModeChange"
      />

      <div v-if="createMode === 'generate'" class="tw-pt-3">
        <p class="tw-text-sm tw-text-grey-8 tw-mb-3">
          Укажите объёмы работ (целые числа) для формирования исполнительной схемы. После создания дождитесь PDF в разделе «Документы» и подпишите документ.
        </p>
        <q-form @submit.prevent="onSubmitGenerate" class="form-1c">
          <div class="exec-list-create-document__table tw-overflow-x-auto tw-mb-4">
            <table class="tw-w-full tw-text-sm tw-border-collapse">
              <thead>
                <tr class="tw-border-b tw-text-left">
                  <th class="tw-py-2 tw-pr-2">Работа</th>
                  <th class="tw-py-2 tw-w-[140px]">Объём <span class="required">*</span></th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(row, idx) in formRows"
                  :key="row.rowKey"
                  class="tw-border-b tw-border-grey-4"
                >
                  <td class="tw-py-2 tw-pr-2 tw-align-top">
                    <span>{{ row.work_set_name }}</span>
                    <span v-if="row.unit_name" class="tw-text-grey-7 tw-ml-1">({{ row.unit_name }})</span>
                    <span v-if="row.placement_type_name" class="tw-text-grey-7 tw-block tw-text-xs tw-mt-0.5">{{ row.placement_type_name }}</span>
                  </td>
                  <td class="tw-py-2 tw-align-top">
                    <CustomInput
                      type="number"
                      :required="true"
                      :allowNegative="false"
                      :value="displayValue(idx)"
                      :suffix="row.unit_name || ''"
                      @change_input="(v) => onVolumeChange(idx, v)"
                    />
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <CustomModalFooter
            submit-btn-text="Создать документ"
            :btn-loading="submitting || pollingPdf"
          />
        </q-form>
      </div>
      <div v-else class="tw-pt-3">
        <p class="tw-text-sm tw-text-grey-8 tw-mb-3">
          Загрузите готовый PDF исполнительной схемы (до 30 МБ). Объёмы для БД подставятся из сметы по текущей группе вызова; файл будет сохранён как документ.
        </p>
        <q-form @submit.prevent="onSubmitUpload" class="form-1c">
          <q-file
            v-model="uploadFile"
            outlined
            dense
            clearable
            accept=".pdf,application/pdf"
            label="Файл PDF"
            class="tw-mb-4"
            :disable="submitting || pollingPdf"
          />
          <CustomModalFooter
            :submit-btn-text="isManuallySigned ? 'Загрузить подписанный' : 'Загрузить и создать'"
            :btn-loading="submitting || pollingPdf"
          />
        </q-form>
      </div>
    </div>
    <q-inner-loading
      :showing="submitting || pollingPdf || loadingSchemaId"
      :label="innerLoadingLabel"
      color="primary"
    />
  </div>
</template>

<script>
import CustomInput from '@/components/form/CustomInput'
import CustomModalFooter from '@/components/modal/modals/modal-footer'
import CustomTabs from '@/components/common/CustomTabs'
import {
  createExecListDocument,
  createExecListManual,
  reformExecListDocument,
  validateExecListBeforeOkk,
} from '@/views/ResidentialSettings/services'
import { useCustomSnackbar } from '@/_helpers/hooks/useCustomSnackbar'
import { useAppStore } from '@/services/store/app'
import { getSchemaIdByWorkSetCheckGroup } from '@/views/EmployeeTasks/services'

function parseInitialVolume(work_set_amount) {
  if (work_set_amount == null || work_set_amount === '') return ''
  const s = String(work_set_amount).trim().replace(',', '.')
  if (!s || Number.isNaN(Number(s))) return ''
  return s
}

export default {
  name: 'ExecListCreateDocument',
  components: { CustomInput, CustomModalFooter, CustomTabs },
  props: {
    data: { type: Object, required: true },
    closeModal: { type: Function, required: true },
  },
  data() {
    return {
      schemaId: null,
      submitting: false,
      pollingPdf: false,
      formRows: [],
      createMode: 'generate',
      createModeTabs: [],
      uploadFile: null,
      isManuallySigned: false,
      loadingSchemaId: false,
    }
  },
  setup() {
    const { showErrorSnackbar, showSucceedSnackbar } = useCustomSnackbar()
    return { showErrorSnackbar, showSucceedSnackbar }
  },
  computed: {
    isManualCreateFlow() {
      const ids = this.data?.manualCreateHelpCallIds
      return Array.isArray(ids) && ids.length > 0 && this.data?.reformFloorMapDocumentId == null
    },
    isLoadingUi() {
      return this.submitting || this.pollingPdf
    },
    innerLoadingLabel() {
      if (this.loadingSchemaId) {
        return 'Загрузка…'
      }
      if (!this.pollingPdf) {
        return this.createMode === 'upload' ? 'Загрузка документа…' : 'Создание документа…'
      }
      return this.createMode === 'upload'
        ? 'Проверяем готовность PDF…'
        : 'Создаётся исполнительная схема, подождите…'
    },
  },
  mounted() {
    const rows = this.data?.rows || []
    this.formRows = rows.map((r) => {
      const keyPlacement =
        r.legend_placement_type_id != null && r.legend_placement_type_id !== ''
          ? r.legend_placement_type_id
          : r.placement_type_id;
      return {
      rowKey:
        keyPlacement != null && keyPlacement !== ''
          ? `${keyPlacement}_${r.work_set_id}`
          : String(r.work_set_id),
      placement_type_id: r.legend_placement_type_id || r.placement_type_id,
      legend_placement_type_id: r.legend_placement_type_id,
      placement_type_name: r.placement_type_name || '',
      work_set_id: r.work_set_id,
      work_set_name: r.work_set_name || '—',
      unit_name: r.unit_name || '',
      volumeStr: parseInitialVolume(r.work_set_amount),
    };
    }),
    this.getSchemaId()
  },
  methods: {
    async getSchemaId() {
      if (this.isManualCreateFlow) {
        this.isManuallySigned = true;
        this.createModeTabs = [
          { name: 'generate', label: 'Сформировать новую' },
          { name: 'upload', label: 'Подписанный документ PDF' },
        ]
        return
      }
      if (this.data.generateOnly) {
        this.createMode = 'generate'
        this.createModeTabs = [
          { name: 'generate', label: 'Сформировать новую' },
        ]
        return
      }
      this.loadingSchemaId = true
      const schemaId = await getSchemaIdByWorkSetCheckGroup(this.data.workSetCheckGroupId, this.data.projectId)
      this.loadingSchemaId = false
      this.schemaId = schemaId
      if (!schemaId) {
        this.createModeTabs = [
          { name: 'generate', label: 'Сформировать новую' },
          { name: 'upload', label: 'Готовый документ PDF' },
        ]
      } else if (schemaId === 2 || schemaId === 5) {
        this.createModeTabs = [
          { name: 'generate', label: 'Сформировать новую' },
        ]
      } else {
        this.createMode = 'upload'
        this.createModeTabs = [
          { name: 'upload', label: 'Готовый документ PDF' },
        ]
      }
    },
    onCreateModeChange(mode) {
      this.createMode = mode
    },
    displayValue(idx) {
      const v = this.formRows[idx]?.volumeStr
      return v === 0 ? '0' : v === '' || v == null ? '' : String(v)
    },
    onVolumeChange(idx, value) {
      if (!this.formRows[idx]) return
      this.formRows[idx].volumeStr = value === '' || value == null ? '' : String(value)
    },
    buildWorkSetsPayload(upload = false) {
      const list = []
      for (const row of this.formRows) {
        const volumeStr = String(row.volumeStr ?? '').trim().replace(',', '.')
        if (!volumeStr || Number.isNaN(Number(volumeStr)) || Number(volumeStr) < 0) {
          this.showErrorSnackbar('Укажите объём для всех работ')
          return null
        }
        const item = {
          work_set_id: row.work_set_id,
          value: volumeStr,
        }
        if (row.placement_type_id != null && row.placement_type_id !== '') {
          item.placement_type_id = row.placement_type_id
        }
        list.push(item)
      }
      return upload ? JSON.stringify(list) : list
    },
    delay(ms) {
      return new Promise((resolve) => setTimeout(resolve, ms))
    },
    buildPdfWaitMessage(row) {
      let pdfMessage =
        'Файл исполнительного листа ещё формируется. Дождитесь появления PDF в разделе «Документы», затем подпишите его и повторите вызов ИТР.'
      if (row?.document_in_queue) {
        pdfMessage =
          'Документ уже в очереди на формирование PDF. Дождитесь появления файла в разделе «Документы», затем подпишите его и повторите вызов ИТР.'
      } else if (row?.queued_for_generation) {
        pdfMessage =
          'Документ поставлен в очередь на формирование PDF. Дождитесь появления файла в разделе «Документы», затем подпишите его и повторите вызов ИТР.'
      }
      return pdfMessage
    },
    closeConfirmLayer() {
      const store = useAppStore()
      if (this.data.confirmContext === 'secondary') {
        store.closeSecondaryModal()
      } else {
        store.closeModal()
      }
    },
    openPdfStillPendingConfirm(lastRow) {
      const pdfMessage = this.buildPdfWaitMessage(lastRow)
      if (typeof this.data.openConfirmModal === 'function') {
        this.data.openConfirmModal({
          title: 'Исполнительная схема',
          message: pdfMessage,
          btnText: 'Понятно',
          cancelBtnText: 'Закрыть',
          onSubmit: async () => {
            this.closeConfirmLayer()
          },
        })
      } else {
        this.showErrorSnackbar(pdfMessage)
      }
    },
    async pollPdfAfterCreate(floor_map_id, floor_map_document_id, validateParams) {
      this.pollingPdf = true
      let lastValidateRow = null
      try {
        for (let attempt = 0; attempt < 3; attempt++) {
          if (attempt > 0) {
            await this.delay(5000)
          }
          const row = await validateExecListBeforeOkk(floor_map_id, validateParams)
          lastValidateRow = row
          if (row?.has_pdf) {
            this.showSucceedSnackbar('Исполнительная схема сформирована (PDF готов).')
            if (typeof this.data.onSuccess === 'function') {
              this.data.onSuccess(floor_map_document_id)
            }
            this.closeModal()
            return
          }
        }

        if (typeof this.data.onSuccess === 'function') {
          this.data.onSuccess(floor_map_document_id)
        }
        this.closeModal()
        this.openPdfStillPendingConfirm(lastValidateRow)
      } finally {
        this.pollingPdf = false
      }
    },
    async onSubmitGenerate() {
      if (!this.formRows.length) {
        this.showErrorSnackbar('Нет позиций для документа')
        return
      }
      const work_sets = this.buildWorkSetsPayload()
      if (!work_sets) return

      const project_id = this.data.projectId
      const floor_map_id = this.data.floorMapId
      if (!project_id || !floor_map_id) {
        this.showErrorSnackbar('Не указан проект или этаж')
        return
      }

      const reformId = this.data.reformFloorMapDocumentId
      const manualHelpCallIds = Array.isArray(this.data.manualCreateHelpCallIds)
        ? this.data.manualCreateHelpCallIds.filter((id) => id != null && id !== '')
        : []
      const isManualCreate = manualHelpCallIds.length > 0 && reformId == null
      const rowHasPlacement = (r) => r.placement_type_id != null && r.placement_type_id !== ''
      const somePlacement = this.formRows.some(rowHasPlacement)
      const allPlacement = this.formRows.length > 0 && this.formRows.every(rowHasPlacement)
      if (reformId != null && somePlacement && !allPlacement) {
        this.showErrorSnackbar(
          'Для пересборки укажите объём для каждой строки с типом помещения: тип должен быть задан у всех позиций.'
        )
        return
      }
      if (isManualCreate && somePlacement && !allPlacement) {
        this.showErrorSnackbar(
          'Укажите объём для каждой строки с типом помещения: тип должен быть задан у всех позиций.'
        )
        return
      }

      this.submitting = true
      const body = {
        project_id,
        work_sets,
        work_set_check_group_id: this.data.workSetCheckGroupId,
      }
      const omitSinglePlacement = (reformId != null || isManualCreate) && allPlacement
      if (!omitSinglePlacement) {
        body.placement_type_id = this.data.placementTypeId
      }
      if (reformId != null) {
        body.floor_map_document_id = reformId
      }
      if (isManualCreate) {
        body.help_call_ids = manualHelpCallIds
      }
      let res = null
      if (reformId != null) {
        res = await reformExecListDocument(floor_map_id, body, this.data.documentParams || {})
      } else if (isManualCreate) {
        res = await createExecListManual(floor_map_id, body, this.data.documentParams || {})
      } else {
        res = await createExecListDocument(floor_map_id, body, this.data.documentParams || {})
      }
      this.submitting = false
      if (!res) return

      const floor_map_document_id = res.floor_map_document_id
      if (floor_map_document_id == null) {
        this.showErrorSnackbar('Не удалось создать документ')
        return
      }

      if (reformId != null || isManualCreate) {
        this.closeModal()
        this.showSucceedSnackbar()

        if (typeof this.data.onSuccess === 'function') {
          this.data.onSuccess(floor_map_document_id)
        }
        return
      }

      const validateParams = {
        project_id,
        placement_type_id: this.data.placementTypeId,
        work_set_check_group_id: this.data.workSetCheckGroupId,
        ...(this.data.documentParams || {}),
      }

      await this.pollPdfAfterCreate(floor_map_id, floor_map_document_id, validateParams)
    },
    async onSubmitUpload() {
      if (!this.uploadFile) {
        this.showErrorSnackbar('Выберите PDF-файл')
        return
      }
      const project_id = this.data.projectId
      const floor_map_id = this.data.floorMapId

      let work_sets = this.buildWorkSetsPayload(true)
      if (!work_sets) return

      if (!project_id || !floor_map_id) {
        this.showErrorSnackbar('Не указан проект или этаж')
        return
      }

      const manualHelpCallIds = Array.isArray(this.data.manualCreateHelpCallIds)
        ? this.data.manualCreateHelpCallIds.filter((id) => id != null && id !== '')
        : []
      const isManualCreate = manualHelpCallIds.length > 0 && this.data.reformFloorMapDocumentId == null

      if (!isManualCreate) {
        if (this.data.placementTypeId == null || this.data.workSetCheckGroupId == null) {
          this.showErrorSnackbar('Не указаны тип помещения или группа вызова')
          return
        }
      }

      const fd = new FormData()
      fd.append('project_id', String(project_id))
      fd.append('work_set_check_group_id', String(this.data.workSetCheckGroupId))
      fd.append('work_sets', work_sets)
      fd.append('exec_list_pdf', this.uploadFile)

      if (isManualCreate) {
        manualHelpCallIds.forEach((id) => fd.append('help_call_ids', String(id)))
        if (this.isManuallySigned && this.createMode === 'upload') {
          fd.append('is_manually_signed', 'true')
        }
        const rowHasPlacement = (r) => r.placement_type_id != null && r.placement_type_id !== ''
        const allPlacement = this.formRows.length > 0 && this.formRows.every(rowHasPlacement)
        if (!allPlacement && this.data.placementTypeId != null) {
          fd.append('placement_type_id', String(this.data.placementTypeId))
        }
      } else {
        fd.append('placement_type_id', String(this.data.placementTypeId))
      }

      this.submitting = true
      const res = isManualCreate
        ? await createExecListManual(floor_map_id, fd, this.data.documentParams || {})
        : await createExecListDocument(floor_map_id, fd, this.data.documentParams || {})
      this.submitting = false
      if (!res) return

      const floor_map_document_id = res.floor_map_document_id
      if (floor_map_document_id == null) {
        this.showErrorSnackbar('Не удалось создать документ')
        return
      }

      if (isManualCreate) {
        this.closeModal()
        this.showSucceedSnackbar(
          this.isManuallySigned
            ? 'Подписанная исполнительная схема загружена'
            : 'Исполнительная схема загружена'
        )
        if (typeof this.data.onSuccess === 'function') {
          this.data.onSuccess(floor_map_document_id)
        }
        return
      }

      const validateParams = {
        project_id,
        placement_type_id: this.data.placementTypeId,
        work_set_check_group_id: this.data.workSetCheckGroupId,
        ...(this.data.documentParams || {}),
      }

      await this.pollPdfAfterCreate(floor_map_id, floor_map_document_id, validateParams)
    },
  },
}
</script>
